/**
 * Stock Adjustments — list and composer pages for manual stock corrections.
 *
 * `StockAdjustmentsPage` — list with All / In / Out tabs.
 * `StockAdjustmentComposerPage` — form to record a new adjustment.
 *
 * @see packages/api-contract/src/procedures/stock.ts — adjustInput / adjustmentRow
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  IndianRupee,
  Plus,
  SlidersHorizontal,
} from "lucide-react";
import type { stock as stockContract } from "@flint/api-contract";
import type { z } from "zod";
import {
  Card,
  CardBody,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from "@flint/ui";
import { ListPagination, avatarClassFor, initialsFor } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { formatDate, paiseToInrShort, rupeesToPaise } from "../../../lib/format.ts";
import "./stock-adjustments-page.css";

type AdjustmentRow = z.infer<typeof stockContract.adjustmentRow>;
type DirectionTab = "all" | "in" | "out";

const PAGE_SIZE = 50;

// ── List page ─────────────────────────────────────────────────────

export function StockAdjustmentsPage() {
  const [dirTab, setDirTab] = useState<DirectionTab>("all");
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];

  // Direction is a SERVER filter (not a client slice), so the In / Out tabs
  // page correctly across the whole dataset.
  useEffect(() => {
    setCursorStack([]);
  }, [dirTab]);

  const list = trpc.stock.adjustments.useQuery(
    {
      limit: PAGE_SIZE,
      ...(cursor !== undefined ? { cursor } : {}),
      ...(dirTab !== "all" ? { direction: dirTab } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const rows = (list.data?.items as AdjustmentRow[] | undefined) ?? [];
  const summary = list.data?.summary ?? {
    total: 0,
    inCount: 0,
    outCount: 0,
    inValuePaise: "0",
    outValuePaise: "0",
  };
  const netValuePaise = (BigInt(summary.inValuePaise) - BigInt(summary.outValuePaise)).toString();

  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };

  return (
    <div className="px-6 pt-5 pb-16 max-w-[1320px] mx-auto w-full space-y-5">
      {/* ── Page head ─────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight leading-tight">
            Stock adjustments
          </h1>
          <p className="mt-1 text-sm text-muted">Physical count corrections and manual changes</p>
        </div>
        <Link
          to="/inventory/stock-adjustments/new"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2 shrink-0"
        >
          <Plus size={13} aria-hidden />
          New adjustment
        </Link>
      </header>

      {/* ── KPI strip ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiTile
          accent="sky"
          icon={<SlidersHorizontal size={14} strokeWidth={2} />}
          label="Total adjustments"
          value={String(summary.total)}
          meta="corrections & write-offs"
        />
        <KpiTile
          accent="emerald"
          icon={<ArrowDown size={14} strokeWidth={2} />}
          label="Stock in"
          value={String(summary.inCount)}
          meta={`${paiseToInrShort(summary.inValuePaise)} added`}
        />
        <KpiTile
          accent="rose"
          icon={<ArrowUp size={14} strokeWidth={2} />}
          label="Stock out"
          value={String(summary.outCount)}
          meta={`${paiseToInrShort(summary.outValuePaise)} removed`}
        />
        <KpiTile
          accent="amber"
          icon={<IndianRupee size={14} strokeWidth={2} />}
          label="Net value"
          value={paiseToInrShort(netValuePaise)}
          meta="net stock-value change"
        />
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────── */}
      <TabsRoot value={dirTab} onValueChange={(v) => setDirTab(v as DirectionTab)}>
        <TabsList aria-label="Direction filter">
          <TabsTrigger value="all" count={summary.total}>All</TabsTrigger>
          <TabsTrigger value="in" count={summary.inCount}>In</TabsTrigger>
          <TabsTrigger value="out" count={summary.outCount} countTone="warn">Out</TabsTrigger>
        </TabsList>
      </TabsRoot>

      {/* ── Body ──────────────────────────────────────────────────── */}
      {list.isLoading ? (
        <p className="text-sm text-muted py-4">Loading adjustments…</p>
      ) : list.error ? (
        <div className="rounded-xl border border-line bg-paper p-12 text-center">
          <h3 className="font-semibold text-ink mb-1">Couldn't load adjustments</h3>
          <p className="text-sm text-muted">
            Something went wrong loading this list. Please refresh the page and try again.
          </p>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-line bg-paper p-12 text-center">
          <SlidersHorizontal size={32} className="mx-auto text-muted/40 mb-3" aria-hidden />
          <h3 className="font-semibold text-ink mb-1">
            {dirTab === "all" ? "No adjustments yet" : `No ${dirTab === "in" ? "inbound" : "outbound"} adjustments`}
          </h3>
          <p className="text-sm text-muted mb-4 max-w-xs mx-auto">
            {dirTab === "all"
              ? "Record a physical count correction or write-off."
              : "Switch tabs or record a new adjustment."}
          </p>
          <Link
            to="/inventory/stock-adjustments/new"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2"
          >
            <Plus size={13} aria-hidden /> New adjustment
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-line bg-paper shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] table-fixed text-sm" aria-label="Stock adjustments">
              <colgroup>
                <col style={{ width: 110 }} />
                <col style={{ width: 220 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 100 }} />
                <col />
              </colgroup>
              <thead>
                <tr className="border-b border-line bg-bg">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">Date</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">Item</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted hidden lg:table-cell">Warehouse</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">Direction</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted">Qty</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted hidden md:table-cell">Rate</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted">Value</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted hidden xl:table-cell">Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <AdjustmentTableRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer stats */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line bg-bg px-4 py-2.5 text-xs text-muted">
            <span className="inline-flex items-center gap-1.5">
              Showing{" "}
              <strong className="font-mono tabular-nums font-bold text-ink">{rows.length}</strong> /{" "}
              <strong className="font-mono tabular-nums font-bold text-ink">{summary.total}</strong>
            </span>
            <span aria-hidden className="h-3 w-px bg-line-2" />
            <span className="inline-flex items-center gap-1.5 text-emerald-700">
              In <strong className="font-mono tabular-nums font-bold">{summary.inCount}</strong>
            </span>
            <span aria-hidden className="h-3 w-px bg-line-2" />
            <span className="inline-flex items-center gap-1.5 text-rose-600">
              Out <strong className="font-mono tabular-nums font-bold">{summary.outCount}</strong>
            </span>
          </div>

          <div className="border-t border-line">
            <ListPagination
              pageNumber={pageNumber}
              showingFrom={showingFrom}
              showingTo={showingTo}
              sortLabel="newest first"
              hasPrev={cursorStack.length > 0}
              hasNext={!!list.data?.nextCursor}
              onPrev={handlePrev}
              onNext={handleNext}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── KPI tile — mirrors the stock summary / warehouses KpiTile ─────

type Accent = "sky" | "indigo" | "emerald" | "amber" | "rose" | "muted";

const ACCENT_BAR: Record<Accent, string> = {
  sky: "bg-sky-500",
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-400",
  rose: "bg-rose-500",
  muted: "bg-line",
};
const ACCENT_ICON: Record<Accent, string> = {
  sky: "bg-sky-50 text-sky-600",
  indigo: "bg-indigo-50 text-indigo-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  rose: "bg-rose-50 text-rose-600",
  muted: "bg-bg text-muted",
};

function KpiTile({
  accent,
  icon,
  label,
  value,
  meta,
}: {
  accent: Accent;
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
}) {
  return (
    <Card className="relative min-w-0 overflow-hidden transition-all hover:-translate-y-px hover:border-line-2 hover:shadow-md">
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${ACCENT_BAR[accent]}`} />
      <CardBody className="px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted">
            <p className="min-w-0 truncate">{label}</p>
            <span
              aria-hidden
              className={`grid size-7 shrink-0 place-items-center rounded-md ${ACCENT_ICON[accent]}`}
            >
              {icon}
            </span>
          </div>
          <p className="text-xl font-bold leading-tight text-ink truncate">{value}</p>
          <p className="mt-1 text-xs text-muted truncate">{meta}</p>
        </div>
      </CardBody>
    </Card>
  );
}

function AdjustmentTableRow({ row }: { row: AdjustmentRow }) {
  const isIn = row.direction === "in";
  return (
    <tr className="border-t border-line hover:bg-raised transition-colors">
      {/* Date */}
      <td className="px-4 py-3">
        <span className="font-mono text-xs text-muted tabular-nums whitespace-nowrap">
          {formatDate(row.createdAt)}
        </span>
      </td>

      {/* Item — avatar + name + SKU */}
      <td className="px-4 py-3">
        <Link
          to="/sales/items/$itemId"
          params={{ itemId: row.itemId }}
          className="flex items-center gap-2.5 min-w-0 group"
        >
          <span
            aria-hidden
            className={`grid h-7 w-7 shrink-0 place-items-center rounded-md text-xs font-bold text-white ${avatarClassFor(row.itemName)}`}
          >
            {initialsFor(row.itemName)}
          </span>
          <div className="min-w-0">
            <span className="block truncate font-semibold text-ink text-sm leading-tight group-hover:text-sky-600">
              {row.itemName}
            </span>
            {row.itemSku ? (
              <span className="block truncate font-mono text-xs text-muted">{row.itemSku}</span>
            ) : null}
          </div>
        </Link>
      </td>

      {/* Warehouse */}
      <td className="px-3 py-3 hidden lg:table-cell">
        <span className="text-xs text-ink-2 truncate block">{row.warehouseName}</span>
      </td>

      {/* Direction chip */}
      <td className="px-3 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold border ${
          isIn
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-rose-50 text-rose-600 border-rose-200"
        }`}>
          {isIn ? <ArrowDown size={9} aria-hidden /> : <ArrowUp size={9} aria-hidden />}
          {isIn ? "In" : "Out"}
        </span>
      </td>

      {/* Qty */}
      <td className="px-3 py-3 text-right whitespace-nowrap">
        <span className="font-mono font-semibold text-sm text-ink tabular-nums">
          {row.quantity.toLocaleString("en-IN")}
        </span>
        <span className="font-mono text-xs text-muted ml-0.5">{row.itemUnit}</span>
      </td>

      {/* Rate */}
      <td className="px-3 py-3 hidden md:table-cell">
        <span className="font-mono text-xs text-ink-2 tabular-nums block text-right">
          {paiseToInrShort(row.ratePaise)}
        </span>
      </td>

      {/* Value */}
      <td className="px-3 py-3">
        <span className="font-mono text-sm font-semibold text-ink tabular-nums block text-right">
          {paiseToInrShort(row.amountPaise)}
        </span>
      </td>

      {/* Notes */}
      <td className="px-3 py-3 hidden xl:table-cell">
        <span className="text-xs text-muted truncate block w-full">{row.notes ?? "—"}</span>
      </td>
    </tr>
  );
}

// ── Composer page ─────────────────────────────────────────────────

export function StockAdjustmentComposerPage() {
  const navigate = useNavigate();
  const [itemId, setItemId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [quantity, setQuantity] = useState("");
  const [rateRupees, setRateRupees] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState("");

  const itemsList = trpc.items.list.useQuery({ limit: 100 }, { staleTime: 30_000 });
  const warehouseList = trpc.warehouses.list.useQuery(
    { includeInactive: false },
    { staleTime: 30_000 },
  );

  const utils = trpc.useUtils();
  const mutation = trpc.stock.adjust.useMutation({
    onSuccess: () => {
      // Refresh the adjustments list + stock summary so the new adjustment
      // and its balance impact land immediately on next mount. Otherwise the
      // /inventory/stock-adjustments listing serves cached data until manual refresh.
      void utils.stock.invalidate();
      void navigate({ to: "/inventory/stock-adjustments" });
    },
    onError: (err) => setFormError(err.message),
  });

  const items = (itemsList.data?.items ?? []) as Array<{
    id: string;
    name: string;
    sku: string | null;
    unit: string;
  }>;
  const warehouses = warehouseList.data?.warehouses ?? [];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    if (!itemId) { setFormError("Item is required."); return; }
    if (!warehouseId) { setFormError("Warehouse is required."); return; }
    const qty = parseInt(quantity, 10);
    if (!qty || qty < 1) { setFormError("Quantity must be a positive integer."); return; }
    const ratePaise = rupeesToPaise(rateRupees);
    if (ratePaise === null || ratePaise < 1n) {
      setFormError("Rate must be a valid positive amount in rupees.");
      return;
    }
    if (!reason.trim()) { setFormError("Reason is required."); return; }

    mutation.mutate({
      itemId,
      warehouseId,
      direction,
      quantity: qty,
      ratePaise: ratePaise.toString(),
      reason: reason.trim(),
      notes: notes.trim() || undefined,
    });
  };

  return (
    <div className="stock-adjustment-composer">
      {/* ── Header — static flex child, never scrolls ─────────────── */}
      <header className="sac-head">
        <Link to="/inventory/stock-adjustments" className="sac-head-back" aria-label="Back to adjustments">
          <ArrowLeft size={16} strokeWidth={2.2} />
        </Link>
        <div className="sac-head-icon" aria-hidden>
          <SlidersHorizontal size={16} />
        </div>
        <div className="sac-head-text">
          <h1>New stock adjustment</h1>
        </div>
        <span className="sac-status-chip">New</span>
      </header>

      {/* ── Scrollable form body ───────────────────────────────────── */}
      {/* `.sac-body` is the full-width scroller so its scrollbar rides the true
          viewport right edge; `.sac-body-inner` is the 760px centered column
          that holds the section cards. See the CSS for the split rationale. */}
      <form id="sac-form" className="sac-body" onSubmit={handleSubmit}>
        <div className="sac-body-inner">
        <div className="sac-section">
          <div className="sac-section-head">
            <span className="sac-step">1</span>
            <div>
              <h3>Adjustment details</h3>
              <span className="sac-why">Select the item, warehouse, and quantity to adjust.</span>
            </div>
          </div>
          <div className="sac-section-body">
            {/* Item */}
            <div className="sac-field">
              <label>
                Item <span className="sac-req">*</span>
              </label>
              <Select value={itemId} onValueChange={setItemId}>
                <SelectTrigger className="!h-9 !text-sm">
                  <SelectValue placeholder="Select an item…" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.sku ? `${item.sku} — ` : ""}{item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Warehouse */}
            <div className="sac-field">
              <label>
                Warehouse <span className="sac-req">*</span>
              </label>
              <Select value={warehouseId} onValueChange={setWarehouseId}>
                <SelectTrigger className="!h-9 !text-sm">
                  <SelectValue placeholder="Select a warehouse…" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Direction */}
            <div className="sac-field">
              <span className="sac-label">
                Direction <span className="sac-req">*</span>
              </span>
              <div className="sac-dir-toggle" role="radiogroup" aria-label="Adjustment direction">
                <label className={`sac-dir-btn ${direction === "in" ? "is-selected is-in" : ""}`}>
                  <input
                    type="radio"
                    name="direction"
                    value="in"
                    checked={direction === "in"}
                    onChange={() => setDirection("in")}
                  />
                  <span className="sac-dir-btn-label">In</span>
                  <span className="sac-dir-btn-hint">stock arrived</span>
                </label>
                <label className={`sac-dir-btn ${direction === "out" ? "is-selected is-out" : ""}`}>
                  <input
                    type="radio"
                    name="direction"
                    value="out"
                    checked={direction === "out"}
                    onChange={() => setDirection("out")}
                  />
                  <span className="sac-dir-btn-label">Out</span>
                  <span className="sac-dir-btn-hint">stock removed</span>
                </label>
              </div>
            </div>

            {/* Quantity + Rate */}
            <div className="sac-grid-2">
              <div className="sac-field">
                <label>
                  Quantity <span className="sac-req">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  style={{ textAlign: "right", fontFamily: "'Geist Mono', monospace" }}
                />
              </div>
              <div className="sac-field">
                <label>
                  Rate ₹/unit <span className="sac-req">*</span>
                </label>
                <div className="sac-rate-wrap">
                  <span className="sac-rate-prefix">₹</span>
                  <input
                    type="number"
                    min={0.01}
                    step={0.01}
                    value={rateRupees}
                    onChange={(e) => setRateRupees(e.target.value)}
                    placeholder="0.00"
                    className="sac-rate-input"
                    style={{ textAlign: "right", fontFamily: "'Geist Mono', monospace" }}
                  />
                </div>
                <span className="sac-help">Used for WAC recalculation</span>
              </div>
            </div>

            {/* Reason */}
            <div className="sac-field">
              <label>
                Reason <span className="sac-req">*</span>
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Physical count correction, Damaged goods…"
                maxLength={300}
              />
            </div>

            {/* Notes */}
            <div className="sac-field">
              <label>
                Notes <span className="sac-opt">optional</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional context…"
                rows={3}
                maxLength={500}
              />
            </div>

            {formError && (
              <div className="sac-error" role="alert">
                <p>{formError}</p>
              </div>
            )}
          </div>
        </div>
        </div>
      </form>

      {/* ── Footer — static flex child, never scrolls ─────────────── */}
      <footer className="sac-foot">
        <div className="sac-foot-actions">
          <button
            type="submit"
            form="sac-form"
            disabled={mutation.isPending}
            className="inline-flex items-center h-9 px-4 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? "Saving…" : "Save adjustment"}
          </button>
          <button
            type="button"
            onClick={() => void navigate({ to: "/inventory/stock-adjustments" })}
            className="inline-flex items-center h-9 px-4 rounded-md border border-line bg-paper text-sm font-semibold text-ink-2 hover:bg-bg"
          >
            Cancel
          </button>
        </div>
      </footer>
    </div>
  );
}
